<template>
  <ElPopover
    placement="right-start"
    width="300"
    trigger="hover"
    :open-delay="300"
    :show-arrow="false"
    popper-class="edition-upgrade-popover"
  >
    <div data-theme="patsnapRD" :data-type="currentEditionPopoverType">
      <div class="mt-[-28px] -mx-4">
        <img :src="trail2upgrade" alt="" />
      </div>
      <div>
        <h3 class="text-[24px] font-bold mb-1 mt-3 text-eureka-600 leading-[32px]">
          {{ t('packageDiff.singleAirUpgrade.title') }}
        </h3>
        <p class="text-sm text-gray-90">{{ t('packageDiff.singleAirUpgrade.description') }}</p>
      </div>
      <ul
        class="flex flex-col gap-2 rounded-lg bg-gradient-to-b from-[#EDF4FC] to-[rgba(237,244,252,0.4)] p-3 my-2"
      >
        <li
          v-for="(feature, key) in tm('packageDiff.singleAirUpgrade.features')"
          :key="key"
          class="flex text-sm text-grey-450 gap-1"
        >
          <PtIcon
            :icon="SolidTickCircle"
            class="th-btn-icon text-xl fill-eureka-500 flex-shrink-0"
          />
          <span v-html="feature" />
        </li>
      </ul>
      <button class="th-btn w-full th-btn-primary" @click="handleUpgrade">
        {{ t('packageDiff.singleAirUpgrade.buttonText') }}
      </button>
    </div>
    <template #reference>
      <div
        v-show="isEditionVisible"
        slot="reference"
        class="invisible lg:visible edition-upgrade-tag relative flex justify-end items-center w-full px-2.5 py-5 pb-6 bg-white/40 text-eureka-500 rounded-lg cursor-pointer"
        :class="{
          'is-closed': !open,
          'is-trial': hasTrialPackage,
        }"
      >
        <template v-if="open">
          <img
            class="absolute bottom-0 left-0 w-[120px] rounded-xl"
            src="../../assets/img/rocket.png"
          />
          <div class="flex flex-col gap-2 items-center relative z-10">
            <span class="text-[12px] mr-1">
              <span>{{ t('editionTag.freemium') }}</span>
            </span>
            <button
              class="edition-upgrade-btn text-xs th-btn py-[2px] pl-[4px] pr-[8px] bg-white/80 text-[#0764e9] active:!bg-white active:!shadow-none"
              @click="handleUpgrade"
            >
              <PtIcon :icon="UpgradeIcon" class="th-btn-icon text-xl" />
              {{ t('editionTag.upgrade') }}
            </button>
          </div>
        </template>
        <PtIcon
          v-else
          :icon="UpgradeIcon"
          class="th-btn-icon text-xl cursor-pointer"
          @click="handleUpgrade"
        />
      </div>
    </template>
  </ElPopover>
</template>

<script setup lang="ts">
import { PtIcon } from 'patsnap-biz';
import { useI18n } from 'vue-i18n';
import { ElPopover } from 'element-plus';
import UpgradeIcon from '../../assets/icon/upgrade.svg';
import trail2upgrade from '../../assets/img/trail2upgrade.png';
import SolidTickCircle from '@patsnap-ui/icon/assets/solid/TickCircle.svg';
import { cn, en, jp, tw } from '../../infrastructure/locales';
import { useEditionTag } from '../../composables/use-edition-tag';
import { useEditionVisibilityWithRouteMeta } from '../../composables/use-edition-visibility-with-route-meta';
import commonProductPackageMessage from '@eureka/language-tool/locales/common-product/package';
import _ from 'lodash';

const props = defineProps<{
  isLogin: boolean;
  open: boolean | undefined;
}>();

const { t, tm } = useI18n({
  messages: _.merge(
    {
      cn,
      en,
      jp,
      tw,
    },
    commonProductPackageMessage,
  ),
});

const {
  currentEditionPopoverType,
  isEditionVisible: baseVisibility,
  // handleUpgrade,
  hasTrialPackage,
} = useEditionTag(props);

const { isEditionVisible } = useEditionVisibilityWithRouteMeta(baseVisibility);

const emits = defineEmits(['handleUpgrade'])

const handleUpgrade = () => {
  emits('handleUpgrade')
};
</script>

<style lang="scss">
.edition-upgrade-popover {
  border-radius: 20px 20px 0 0 !important;
  padding: 0 16px 24px !important;
  border: none !important;
  text-align: left !important;
  word-break: normal !important;
}
.edition-upgrade-tag {
  @apply mt-10;
  background: linear-gradient(92.16deg, #e6f8dd 0%, #edfcff 27.51%, #ddf0f8 62.46%, #b9d9ff 99.92%);
  &.is-closed {
    @apply py-[6px] px-2 mt-0;
    background: white;
    width: auto;
  }

  &.is-trial {
    background: linear-gradient(
      92.16deg,
      #e6f8dd 0%,
      #edfcff 27.51%,
      #ddf0f8 62.46%,
      #b9d9ff 99.92%
    );
    @apply text-primary;
  }

  .edition-upgrade-btn {
    height: 24px;
    min-height: 24px;
    border: none;
    &:hover {
      box-shadow:
        0px 0px 4px 0px rgba(7, 100, 233, 0.14),
        0px 4px 8px 0px rgba(7, 100, 233, 0.08);
      background-color: white;
    }
  }
}
</style>
